import PropTypes from 'prop-types';
import { useState } from 'react';
import { IconPaperclip } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import prepareTranslate from '@/helpers/dictionary';
import { downloadFile } from '@/helpers/download';
import { splitFileName } from '@/helpers/string';
import { CUSTOM_MIME_TYPES } from '@/helpers/enums';
import vars from '@/config/vars';
import styles from './index.module.css';

const allowedExtensions = Object.values(CUSTOM_MIME_TYPES).flatMap(({ extensions }) => extensions);
const allowedExtensionsLabel = allowedExtensions.join(', ');

/**
 * The email body editor used by the email log, built to behave like the one on
 * the commissions page: the html body is edited in place, files can be dragged
 * onto it or picked with the paperclip, and any attachment can be removed.
 *
 * `contentRef` gives the owner access to the edited html at save time.
 */
const EmailBodyEditor = (props) => {
  const {
    label,
    content,
    contentRef,
    keptFiles = [],
    newFiles = [],
    onAddFiles,
    onRemoveKeptFile,
    onRemoveNewFile,
  } = props;

  const t = prepareTranslate();
  const [isDragging, setIsDragging] = useState(false);

  const filesToBase64 = (fileList) => Promise.all(
    fileList.map((file) => new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve({
        fileData: reader.result.split(',')[1],
        name: file.name,
        file,
      });
      reader.readAsDataURL(file);
    })),
  );

  const validateFiles = (fileList) => {
    const maxFileSizeInBytes = vars.maxFileSize * 1024 ** 2;
    const validFiles = [];
    const oversizeNames = [];
    const invalidTypeNames = [];

    fileList.forEach((file) => {
      const ext = `.${file.name.split('.').pop().toLowerCase()}`;
      if (file.size > maxFileSizeInBytes) {
        oversizeNames.push(file.name);
      } else if (!allowedExtensions.includes(ext)) {
        invalidTypeNames.push(file.name);
      } else {
        validFiles.push(file);
      }
    });

    if (oversizeNames.length > 0) {
      notifications.show({
        title: t('components.customDropZone.notification.invalidFileSize.title'),
        message: `${t('components.customDropZone.notification.invalidFileSize.message')} ${oversizeNames.join(', ')}`,
        autoClose: false,
      });
    }

    if (invalidTypeNames.length > 0) {
      notifications.show({
        title: t('components.customDropZone.notification.invalidFileFormat.title'),
        message: `${t('components.customDropZone.notification.invalidFileFormat.message')} ${invalidTypeNames.join(', ')}. ${t('components.customDropZone.notification.invalidFileFormat.allow')} ${allowedExtensionsLabel}`,
        autoClose: false,
      });
    }

    return validFiles;
  };

  const addFiles = async (fileList) => {
    const validFiles = validateFiles(fileList);
    if (validFiles.length === 0) return;

    const previews = await filesToBase64(validFiles);
    onAddFiles(previews);
  };

  const handleFileChange = async (event) => {
    await addFiles(Array.from(event.target.files));
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (event) => {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setIsDragging(false);
    }
  };

  const handleDrop = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);

    const droppedFiles = Array.from(event.dataTransfer.files);
    if (droppedFiles.length === 0) return;

    await addFiles(droppedFiles);
  };

  return (
    <>
      <p className={styles.subTitle}>{label}</p>
      <div
        className={`${styles.messageContainer} ${isDragging ? styles.messageContainerDragging : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {isDragging && (
          <div className={styles.dropOverlay}>
            <p className={styles.dropOverlayText}>{t('components.email.dropFiles')}</p>
          </div>
        )}

        <div
          ref={contentRef}
          contentEditable
          suppressContentEditableWarning
          className={styles.messageArea}
          dangerouslySetInnerHTML={{ __html: content }}
        />

        <div className={styles.attachmentWrapper}>
          <label className={styles.clipLabel}>
            <IconPaperclip stroke={1.5} size={20} className={styles.clipIcon} />
            <input
              type='file'
              multiple
              accept={allowedExtensions.join(',')}
              className={styles.fileInput}
              onChange={handleFileChange}
            />
          </label>

          {keptFiles.map((file, index) => (
            <div key={`kept-${file.path}`} className={styles.attachment}>
              <span>{splitFileName(file.name).baseName}</span>
              <span
                className={styles.removeBtn}
                onClick={() => onRemoveKeptFile(index)}
              >✕</span>
            </div>
          ))}

          {newFiles.map((file, index) => (
            <div key={`new-${file.name}`} className={styles.attachment}>
              <span onClick={() => downloadFile(file.fileData, file.name)}>
                {splitFileName(file.name).baseName}
              </span>
              <span
                className={styles.removeBtn}
                onClick={() => onRemoveNewFile(index)}
              >✕</span>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

EmailBodyEditor.propTypes = {
  label: PropTypes.string,
  content: PropTypes.string,
  contentRef: PropTypes.object,
  keptFiles: PropTypes.array,
  newFiles: PropTypes.array,
  onAddFiles: PropTypes.func,
  onRemoveKeptFile: PropTypes.func,
  onRemoveNewFile: PropTypes.func,
};

export default EmailBodyEditor;
